import { isFunction } from 'lodash';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import useSWRImmutable from 'swr/immutable';

import { OrganizationType } from '@gmm/sdk/shared';
import { is404, isAxiosError } from '~/lib/config/axios';
import { useCurrentTeacher } from '~/lib/hooks/useCurrentTeacher';
import { useSnackbar } from '~/lib/hooks/useSnackbar';
import { useFeatureFlag } from '~/lib/launchDarkly';
import { makeStore } from '~/lib/makeStore';
import { ReadOnlyOptions } from '~/lib/sdk';
import { swrKeyFor } from '~/lib/swr';
import { useGeographicalState } from '~/lib/trainerSettings';
import { getEmptyArray } from '~/lib/type-utils';

import { skillDiscoveryErrorStore } from '../hooks/errorStore';
import { CollectionResponse, SkillBankType, Tag } from '../types';
import { useSelectedGrade } from '../useGrades';

const homeschoolColleaguesIds = ['work'] as const;
const colleaguesIds = ['school', 'district', 'work'] as const;
const reservedCollectionNames = [...colleaguesIds, 'search'];

export const isReservedCollectionName = (
  selectedCollection: Tag | null,
): boolean => reservedCollectionNames.includes(selectedCollection?.id || '');

interface CollectionsContext {
  collections: CollectionResponse;
  selectedCollection: Tag | null;
  refreshCollections?: () => void;
}

const [CollectionsProvider, useSetCollectionsState, useCollectionsStore] =
  makeStore<CollectionsContext>({
    name: 'Collections',
    initialState: {
      collections: [],
      selectedCollection: null,
      refreshCollections: undefined,
    },
    localStorageKey: 'skillDiscovery.collections',
    scopeToTeacher: true,
  });

export { CollectionsProvider };

interface UseCollections extends CollectionsContext {
  setSelectedCollection: Dispatch<SetStateAction<Tag | null>>;
}

type UseSelectedCollection = [
  UseCollections['selectedCollection'],
  UseCollections['setSelectedCollection'],
];

export const useSelectedCollection = (): UseSelectedCollection => {
  const { selectedCollection } = useCollectionsStore();
  const setState = useSetCollectionsState();
  const setSelectedCollection = useCallback<UseSelectedCollection[1]>(
    collectionOrCallback => {
      setState(draft => {
        const currentlySelected = draft.selectedCollection;
        const nextSelected = isFunction(collectionOrCallback)
          ? collectionOrCallback(currentlySelected)
          : collectionOrCallback;

        if (nextSelected === currentlySelected) return;

        draft.selectedCollection = nextSelected;
      });
    },
    [setState],
  );

  return [selectedCollection, setSelectedCollection];
};

export const useCollections = ({
  readOnly,
}: ReadOnlyOptions = {}): UseCollections => {
  const [t] = useTranslation(['skillDiscovery']);
  const { data: teacher } = useCurrentTeacher();
  const { enqueueGenericError } = useSnackbar();
  const [selectedGrade, setSelectedGrade] = useSelectedGrade();
  const [state] = useGeographicalState();
  const colleaguesSelected = selectedGrade === 'colleagues';
  const [selectedCollection, setSelectedCollection] = useSelectedCollection();
  const previousSelectedCollection = useRef(selectedCollection);
  const isHomeschooling = useFeatureFlag(
    'Feature.Collections.HideColleagues',
    false,
  );
  const error = skillDiscoveryErrorStore(state => state.error);

  const swrKey = swrKeyFor(() =>
    selectedGrade && !colleaguesSelected
      ? ['collections.show', selectedGrade, state]
      : null,
  );

  // TODO: Use CollectionsShowResponse
  const { data: collections, mutate } = useSWRImmutable<CollectionResponse>(
    swrKey,
    {
      onError: error => {
        if (!isAxiosError(error) || !is404(error)) {
          enqueueGenericError();

          return;
        }

        setSelectedGrade(null);
        setSelectedCollection(null);
      },
      ...(readOnly ? { revalidateOnMount: false } : {}),
    },
  );

  // Refresh on 404
  useEffect(() => {
    if (error && is404(error)) {
      // Clear stale localStorage
      localStorage.removeItem('skillDiscovery.collections');

      // Refetch collections
      mutate();
    }
  }, [error, mutate]);

  // This ensures we always have the correct language
  const getColleagues = useCallback((): Tag[] => {
    const isSchoolTeacher =
      teacher?.organization.organizationType == OrganizationType.School;

    const colleagues = isHomeschooling
      ? homeschoolColleaguesIds
      : colleaguesIds;

    return colleagues
      .filter(id => isSchoolTeacher || id !== 'school')
      .map<Tag>((id, position) => ({
        depth: 1,
        id,
        name: t(`skillDiscovery:colleagues.${id}`),
        // This doesn't really matter as of right now, but for consistency
        nameIdentifier: t(`skillDiscovery:colleagues.${id}`, { lng: 'en' }),
        parentId: 'colleagues',
        path: ['work-and-colleagues', id],
        position,
        type: SkillBankType.Collection,
      }));
  }, [t, teacher, isHomeschooling]);

  useEffect(() => {
    if (colleaguesSelected) return;

    const nextSelectedCollection = collections?.find(
      ({ nameIdentifier }) =>
        nameIdentifier === previousSelectedCollection.current?.nameIdentifier,
    );

    if (nextSelectedCollection) {
      setSelectedCollection(nextSelectedCollection);
    } else if (
      collections &&
      collections[0]?.parentId !== selectedCollection?.parentId
    ) {
      setSelectedCollection(collections[0]);
    }
  }, [
    collections,
    colleaguesSelected,
    selectedCollection?.parentId,
    setSelectedCollection,
  ]);

  useEffect(() => {
    if (selectedCollection) {
      previousSelectedCollection.current = selectedCollection;
    }
  }, [selectedCollection]);

  useEffect(() => {
    if (
      colleaguesSelected &&
      previousSelectedCollection.current?.parentId !== 'colleagues'
    ) {
      setSelectedCollection(getColleagues()[0]);
    }
  }, [colleaguesSelected, getColleagues, setSelectedCollection]);

  return {
    collections: colleaguesSelected
      ? getColleagues()
      : collections ?? getEmptyArray(),
    selectedCollection,
    setSelectedCollection,
    refreshCollections: () => mutate(),
  };
};
